import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/PasswordInput";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { KeyRound, ShieldAlert, Loader2 } from "lucide-react";

export default function ChangePassword() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const forced = !!user?.must_change_password;
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("New password must be at least 8 characters");
    if (next !== confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated — you're all set.");
      if (user) setUser({ ...user, must_change_password: false });
      nav(forced ? "/dashboard" : "/profile", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[hsl(42,45%,96%)]">
      <Card className="clay-card max-w-md w-full p-8" data-testid="change-password-card">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-12 h-12 rounded-2xl bg-[hsl(32,87%,55%)]/15 text-[hsl(32,87%,55%)] flex items-center justify-center flex-shrink-0">
            {forced ? <ShieldAlert size={22}/> : <KeyRound size={22}/>}
          </div>
          <div>
            <div className="uppercase-label">{forced ? "Required" : "Security"}</div>
            <h1 className="font-display font-bold text-2xl">
              {forced ? "Set a new password" : "Change your password"}
            </h1>
            <p className="text-xs text-muted-foreground mt-1">
              {forced
                ? "Your account was created by an admin. Pick a password only you know before you continue."
                : "Enter your current password and the new one you'd like to use."}
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>{forced ? "Temporary password (from your admin)" : "Current password"}</Label>
            <PasswordInput value={current} onChange={e => setCurrent(e.target.value)} required autoFocus data-testid="cp-current"/>
          </div>
          <div>
            <Label>New password</Label>
            <PasswordInput value={next} onChange={e => setNext(e.target.value)} required minLength={8} data-testid="cp-new"/>
            <p className="text-[11px] text-muted-foreground mt-1">At least 8 characters.</p>
          </div>
          <div>
            <Label>Confirm new password</Label>
            <PasswordInput value={confirm} onChange={e => setConfirm(e.target.value)} required data-testid="cp-confirm"/>
          </div>
          <Button type="submit" disabled={busy} className="btn-pill bg-[hsl(149,40%,30%)] hover:bg-[hsl(149,40%,25%)] w-full" data-testid="cp-submit">
            {busy ? <><Loader2 size={14} className="animate-spin mr-2"/>Updating…</> : "Update password"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
